import { useEffect, useRef, useCallback, useState } from "react";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: TurnstileOptions,
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact";
}

interface TurnstileWidgetProps {
  siteKey: string;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact";
  onVerify?: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  className?: string;
  /** When this value changes, the widget will reset and get a fresh token */
  resetKey?: string | number;
  /** When true, don't render the widget (user has a valid cached challenge) */
  skip?: boolean;
}

type ScriptStatus = "loading" | "ready" | "error";

/**
 * Ensures the Turnstile script is loaded and `window.turnstile` is available.
 *
 * The script is normally included in <head> via Document.tsx, but if the user
 * entered through an error boundary (e.g. 404) and then client-side navigated
 * to a login page, the script may be missing. This function detects that case
 * and dynamically injects the script as a fallback.
 */
function ensureTurnstileScript(signal?: AbortSignal): Promise<void> {
  if (window.turnstile) return Promise.resolve();

  return new Promise((resolve, reject) => {
    // Check if a script tag already exists (normal path — loading from <head>)
    const existing = document.querySelector(
      `script[src^="${TURNSTILE_SCRIPT_URL}"]`,
    );

    if (!existing) {
      // Script tag missing entirely (e.g. entered via error boundary) — inject it
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("Failed to load Turnstile script"));
      document.head.appendChild(script);
      signal?.addEventListener("abort", () =>
        reject(new Error("Aborted")),
      );
      return;
    }

    // Script tag exists but hasn't finished loading.
    // Use event listeners + polling fallback (load event may have already fired).
    let settled = false;

    const onReady = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const onFail = (reason: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(reason));
    };

    let attempts = 0;
    const interval = setInterval(() => {
      if (window.turnstile) {
        onReady();
      } else if (++attempts >= 300) {
        onFail("Turnstile script timed out");
      }
    }, 50);

    const handleLoad = () => {
      if (window.turnstile) onReady();
    };
    const handleError = () => onFail("Turnstile script failed to load");

    existing.addEventListener("load", handleLoad);
    existing.addEventListener("error", handleError);

    function cleanup() {
      clearInterval(interval);
      existing!.removeEventListener("load", handleLoad);
      existing!.removeEventListener("error", handleError);
    }

    signal?.addEventListener("abort", () => onFail("Aborted"));
  });
}

/**
 * Cloudflare Turnstile Widget Component
 *
 * Renders the Turnstile challenge widget and handles token generation.
 * The token is automatically added to form submissions via a hidden input.
 *
 * The Turnstile script is loaded globally in <head> via Document.tsx.
 * If the script is missing (e.g. after error boundary recovery), the widget
 * will dynamically inject it as a fallback.
 */
export function TurnstileWidget({
  siteKey,
  theme = "auto",
  size = "normal",
  onVerify,
  onError,
  onExpire,
  className,
  resetKey,
  skip,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [scriptStatus, setScriptStatus] = useState<ScriptStatus>(() =>
    typeof window !== "undefined" && window.turnstile ? "ready" : "loading",
  );

  const handleCallback = useCallback(
    (token: string) => {
      if (inputRef.current) {
        inputRef.current.value = token;
      }
      onVerify?.(token);
    },
    [onVerify],
  );

  const handleError = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onError?.();
  }, [onError]);

  const handleExpire = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onExpire?.();
  }, [onExpire]);

  // Load the Turnstile script and render the widget when visible (not skipped).
  useEffect(() => {
    if (!siteKey || skip) return;

    let cancelled = false;
    const abortController = new AbortController();

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;

      // Clean up existing widget
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore errors from removing non-existent widget
        }
      }

      // Render new widget
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: handleCallback,
        "error-callback": handleError,
        "expired-callback": handleExpire,
        theme,
        size,
      });
    };

    if (window.turnstile) {
      setScriptStatus("ready");
      renderWidget();
    } else {
      setScriptStatus("loading");
      ensureTurnstileScript(abortController.signal)
        .then(() => {
          if (!cancelled) {
            setScriptStatus("ready");
            renderWidget();
          }
        })
        .catch(() => {
          if (!cancelled) {
            setScriptStatus("error");
          }
        });
    }

    return () => {
      cancelled = true;
      abortController.abort();
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore errors
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, skip, theme, size, handleCallback, handleError, handleExpire]);

  // Reset widget when resetKey changes (e.g., after a form submission error)
  // This gets a fresh token since Cloudflare tokens are single-use
  useEffect(() => {
    if (resetKey !== undefined && widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  }, [resetKey]);

  // Always render — never return null.
  // This avoids mount/unmount transition bugs when skip changes.
  const isActive = Boolean(siteKey) && !skip;

  return (
    <div className={isActive ? className : undefined} hidden={!isActive}>
      {isActive && scriptStatus === "loading" && (
        <p className="text-sm text-gray-500">Loading verification...</p>
      )}
      {isActive && scriptStatus === "error" && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">
            Bot verification could not load.{" "}
            <button
              type="button"
              className="font-medium underline"
              onClick={() => window.location.reload()}
            >
              Refresh the page
            </button>{" "}
            to try again.
          </p>
        </div>
      )}
      <div ref={containerRef} />
      {isActive && (
        <input
          ref={inputRef}
          type="hidden"
          name="cf-turnstile-response"
          defaultValue=""
        />
      )}
    </div>
  );
}

export default TurnstileWidget;
